import Phaser from 'phaser';
import { GAME_WIDTH } from '@/config/game-config';
import { COLORS, NEON_FONTS } from '@/config/colors';

const FONT_FAMILY = NEON_FONTS.BODY;
const TITLE_FONT = NEON_FONTS.TITLE;
const HUD_Y = 8;
const SCORE_FONT_SIZE = '16px';
const LABEL_FONT_SIZE = '10px';
const SCORE_ROLL_RATE = 8; // exponential decay rate per second (higher = faster catch-up)
const MIN_ROLL_SPEED = 40; // minimum points per second to ensure small values animate visibly

/** Color mapping for each fruit type */
const FRUIT_ICON_COLORS: Record<string, number> = {
  Cherry: 0xff0000,
  Strawberry: 0xff3366,
  Orange: 0xff8800,
  Apple: 0x00cc00,
  Melon: 0x00ff88,
  Galaxian: 0x0088ff,
  Bell: 0xffdd00,
  Key: 0xcccccc,
};

const FRUIT_ICON_RADIUS = 4;
const FRUIT_ICON_SPACING = 14;
const FRUIT_COUNTER_Y = 38;
const FRUIT_COUNTER_START_X = 12;

export class HudDisplay {
  private readonly scoreLabel: Phaser.GameObjects.Text;
  private readonly scoreText: Phaser.GameObjects.Text;
  private readonly highScoreLabel: Phaser.GameObjects.Text;
  private readonly highScoreText: Phaser.GameObjects.Text;
  private readonly levelText: Phaser.GameObjects.Text;
  private readonly livesGraphics: Phaser.GameObjects.Graphics;
  private readonly fruitGraphics: Phaser.GameObjects.Graphics;

  private displayedScore = 0;
  private targetScore = 0;
  private currentHighScore = 0;
  private lives = 3;
  private collectedFruits: string[] = [];

  constructor(scene: Phaser.Scene) {
    const centerX = GAME_WIDTH / 2;

    const hudDepth = 100;

    // Score label
    this.scoreLabel = scene.add.text(8, HUD_Y, 'SCORE', {
      fontFamily: FONT_FAMILY,
      fontSize: LABEL_FONT_SIZE,
      fontStyle: 'bold',
      color: '#667788',
    }).setDepth(hudDepth);

    // Score value
    this.scoreText = scene.add.text(8, HUD_Y + 14, '0', {
      fontFamily: TITLE_FONT,
      fontSize: SCORE_FONT_SIZE,
      fontStyle: 'bold',
      color: '#FFFFFF',
      shadow: { offsetX: 0, offsetY: 0, color: '#00E5FF', blur: 6, fill: true },
    }).setDepth(hudDepth);

    // High score label
    this.highScoreLabel = scene.add.text(centerX, HUD_Y, 'HIGH SCORE', {
      fontFamily: FONT_FAMILY,
      fontSize: LABEL_FONT_SIZE,
      fontStyle: 'bold',
      color: '#667788',
    }).setOrigin(0.5, 0).setDepth(hudDepth);

    // High score value
    this.highScoreText = scene.add.text(centerX, HUD_Y + 14, '0', {
      fontFamily: TITLE_FONT,
      fontSize: SCORE_FONT_SIZE,
      fontStyle: 'bold',
      color: '#FFE500',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 6, fill: true },
    }).setOrigin(0.5, 0).setDepth(hudDepth);

    // Level indicator
    this.levelText = scene.add.text(GAME_WIDTH - 8, HUD_Y, 'LVL 1', {
      fontFamily: TITLE_FONT,
      fontSize: LABEL_FONT_SIZE,
      fontStyle: 'bold',
      color: '#00FF88',
      shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 6, fill: true },
    }).setOrigin(1, 0).setDepth(hudDepth);

    // Lives display
    this.livesGraphics = scene.add.graphics().setDepth(hudDepth);
    this.drawLives();

    // Fruit counter display
    this.fruitGraphics = scene.add.graphics().setDepth(hudDepth);
  }

  setScore(score: number): void {
    this.targetScore = score;
  }

  setHighScore(highScore: number): void {
    this.currentHighScore = highScore;
    this.highScoreText.setText(String(highScore));
  }

  setLevel(level: number): void {
    this.levelText.setText(`LVL ${level}`);
  }

  setLives(lives: number): void {
    this.lives = lives;
    this.drawLives();
  }

  setCollectedFruits(fruits: ReadonlyArray<string>): void {
    this.collectedFruits = [...fruits];
    this.drawFruitCounter();
  }

  update(delta: number): void {
    if (this.displayedScore < this.targetScore) {
      const diff = this.targetScore - this.displayedScore;
      const deltaSeconds = delta / 1000;
      // Exponential decay for smooth easing: larger gaps close faster
      const proportionalStep = diff * (1 - Math.exp(-SCORE_ROLL_RATE * deltaSeconds));
      // Minimum speed ensures small increments (10 points) still visibly roll
      const minStep = MIN_ROLL_SPEED * deltaSeconds;
      const step = Math.max(1, Math.round(Math.max(proportionalStep, minStep)));
      this.displayedScore = Math.min(this.targetScore, this.displayedScore + step);
      this.scoreText.setText(String(this.displayedScore));
    }

    // Update high score display if current score exceeds it
    if (this.targetScore > this.currentHighScore) {
      this.currentHighScore = this.targetScore;
      this.highScoreText.setText(String(this.currentHighScore));
    }
  }

  private drawLives(): void {
    this.livesGraphics.clear();

    const startX = GAME_WIDTH - 10;
    const y = HUD_Y + 22;
    const radius = 6;
    const spacing = 18;
    const mouthAngle = Math.PI / 6; // 30-degree mouth opening

    for (let i = 0; i < this.lives; i++) {
      const x = startX - i * spacing;
      this.livesGraphics.fillStyle(COLORS.PACMAN, 1);
      // Draw Pac-Man icon (circle with mouth wedge facing left)
      this.livesGraphics.beginPath();
      this.livesGraphics.arc(
        x, y, radius,
        Math.PI + mouthAngle,
        Math.PI - mouthAngle,
        false,
      );
      this.livesGraphics.lineTo(x, y);
      this.livesGraphics.closePath();
      this.livesGraphics.fillPath();
    }
  }

  /** Draw small colored fruit icons in the HUD showing collected bonuses. */
  private drawFruitCounter(): void {
    this.fruitGraphics.clear();

    for (let i = 0; i < this.collectedFruits.length; i++) {
      const fruitName = this.collectedFruits[i];
      if (!fruitName) {
        continue;
      }
      const color = FRUIT_ICON_COLORS[fruitName] ?? 0xffffff;
      const x = FRUIT_COUNTER_START_X + i * FRUIT_ICON_SPACING;
      const y = FRUIT_COUNTER_Y;

      // Draw fruit body (filled circle)
      this.fruitGraphics.fillStyle(color, 1);
      this.fruitGraphics.fillCircle(x, y, FRUIT_ICON_RADIUS);

      // Draw highlight for 3D effect
      this.fruitGraphics.fillStyle(0xffffff, 0.4);
      this.fruitGraphics.fillCircle(x - 1, y - 1, 1.5);

      // Draw stem for applicable fruits
      if (fruitName === 'Cherry' || fruitName === 'Strawberry' || fruitName === 'Apple') {
        this.fruitGraphics.lineStyle(1, 0x006600, 1);
        this.fruitGraphics.beginPath();
        this.fruitGraphics.moveTo(x, y - FRUIT_ICON_RADIUS);
        this.fruitGraphics.lineTo(x + 1, y - FRUIT_ICON_RADIUS - 3);
        this.fruitGraphics.strokePath();
      }
    }
  }

  destroy(): void {
    this.scoreLabel.destroy();
    this.scoreText.destroy();
    this.highScoreLabel.destroy();
    this.highScoreText.destroy();
    this.levelText.destroy();
    this.livesGraphics.destroy();
    this.fruitGraphics.destroy();
  }
}
